import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { PageHero, SectionHeading } from "@/components/site/PageParts";
import { Reveal } from "@/components/site/Reveal";
import { NEWS_POSTS, NEWS_CATEGORIES, formatDate } from "@/data/news";
import { Seo } from "@/components/site/Seo";
import { cn } from "@/lib/utils";

export default function News() {
  const [filter, setFilter] = useState("All");
  const posts = filter === "All" ? NEWS_POSTS : NEWS_POSTS.filter((p) => p.category === filter);

  return (
    <div data-testid="news-page">
      <Seo title="News & Stories — RDA Glasgow" description="Updates from the charity, stories from our riders, and glimpses behind the scenes at RDA Glasgow." />
      <PageHero
        title="News & Stories"
        subtitle="Updates from the charity, stories from our riders, and glimpses behind the scenes."
        image={NEWS_POSTS[2].coverImage}
        imageAlt={NEWS_POSTS[2].coverAlt}
      />

      <section className="bg-rda-offwhite">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          {/* Category chips */}
          <div className="flex flex-wrap gap-3" data-testid="news-filters">
            {NEWS_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                aria-pressed={filter === cat}
                data-testid={`news-filter-${cat.toLowerCase().replace(/\s/g, "-")}`}
                className={cn(
                  "rounded-full px-5 py-2 text-sm font-semibold transition-colors min-h-[44px]",
                  filter === cat
                    ? "bg-rda-teal text-white shadow-soft"
                    : "bg-white text-rda-ink border border-border hover:border-rda-teal hover:text-rda-teal"
                )}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="mt-10 grid gap-8 md:grid-cols-3" data-testid="news-grid">
            {posts.map((post, i) => (
              <Reveal key={post.slug} delay={i * 0.08}>
                <Link
                  to={`/news/${post.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift"
                  data-testid={`news-card-${post.slug}`}
                >
                  <div className="overflow-hidden">
                    <img
                      src={post.coverImage}
                      alt={post.coverAlt}
                      loading="lazy"
                      className="h-52 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="rounded-full bg-rda-teal/10 px-3 py-1 font-semibold text-rda-teal">
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1.5 text-rda-muted">
                        <Calendar className="h-3.5 w-3.5" /> {formatDate(post.date)}
                      </span>
                    </div>
                    <h2 className="mt-4 font-heading font-bold text-xl text-rda-ink leading-snug">
                      {post.title}
                    </h2>
                    <p className="mt-3 text-sm text-rda-muted flex-1">{post.excerpt}</p>
                    <span className="mt-5 inline-flex items-center text-sm font-semibold text-rda-teal">
                      Read more
                      <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          {posts.length === 0 && (
            <p className="mt-10 text-rda-muted" data-testid="news-empty">
              No stories in this category yet — check back soon.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
